/**
 * @author        matt@zumper.com (Matthieu Devlin)
 * @copyright     Copyright (c) 2024, Zumper
 * @description   Sentry initialization for browser
 */

import type { Exception, StackFrame } from '@sentry/types'
import { STATIC_CDN_URL } from '@zcode/nr-utils'

const isThirdPartyFrame = (frame: StackFrame | undefined): boolean => {
  if (frame && frame.filename) {
    const filename = frame.filename
    // Considered 3rd party if filename is of the asc-zumper script or is a url that doesn't start with our CDN url
    if (filename.endsWith('asc-zumper.js')) {
      return true
    }
    // Assuming all resources use https
    return filename.startsWith('https://') && !filename.startsWith(STATIC_CDN_URL)
  }
  return false
}

export const isThirdPartyException = (exceptions: Exception[]): boolean => {
  for (const exception of exceptions) {
    // Check the first 2 frames of the stacktrace - we need to check the second frame because sometimes exceptions get
    // wrapped by sentry so the first frame is sentry code. It is difficult to confirm that with the minified
    // stacktrace, so we will assume that if the second frame is third party, then the exception is from third party
    // code.
    if (
      isThirdPartyFrame(exception.stacktrace?.frames?.[0]) ||
      isThirdPartyFrame(exception.stacktrace?.frames?.[1])
    ) {
      return true
    }
  }
  return false
}
